import { TemplateCode } from '../types/whatsapp-templates.types';

/**
 * Template code constants
 */
export const TEMPLATE_CODES = {
  WELCOME: TemplateCode.WELCOME,
  MEMBERSHIP_PURCHASE: TemplateCode.MEMBERSHIP_PURCHASE,
  MEMBERSHIP_RENEWAL: TemplateCode.MEMBERSHIP_RENEWAL,
  MEMBERSHIP_EXPIRING: TemplateCode.MEMBERSHIP_EXPIRING,
  PAYMENT_REMINDER: TemplateCode.PAYMENT_REMINDER,
  BIRTHDAY: TemplateCode.BIRTHDAY,
  PAYMENT_RECEIPT: TemplateCode.PAYMENT_RECEIPT,
  SHARE_CATALOG: TemplateCode.SHARE_CATALOG,
  SALE_PAYMENT_REMINDER: TemplateCode.SALE_PAYMENT_REMINDER,
} as const;

/**
 * Event names for WhatsApp template events
 */
export const WHATSAPP_TEMPLATE_EVENTS = {
  SEND_TEMPLATE: 'whatsapp/template.send',
  TEMPLATE_SENT: 'whatsapp/template.sent',
  TEMPLATE_FAILED: 'whatsapp/template.failed',
} as const;

/**
 * Template metadata mapping
 */
export const TEMPLATE_METADATA = {
  [TemplateCode.WELCOME]: {
    code: TemplateCode.WELCOME,
    title: 'Mensaje de Bienvenida',
    description: 'Mensaje para nuevos clientes',
    icon: 'UserPlus',
    variables: ['clientName', 'gymName', 'registrationDate'],
    exampleValues: {
      clientName: 'Juan Pérez',
      gymName: 'GymSpace',
      registrationDate: '15/01/2024',
    },
  },
  [TemplateCode.MEMBERSHIP_PURCHASE]: {
    code: TemplateCode.MEMBERSHIP_PURCHASE,
    title: 'Compra de Membresía',
    description: 'Confirmación de nueva membresía',
    icon: 'ShoppingCart',
    variables: ['clientName', 'planName', 'startDate', 'endDate', 'amount', 'paymentFrequency'],
    exampleValues: {
      clientName: 'María López',
      planName: 'Premium',
      startDate: '01/02/2024',
      endDate: '01/03/2024',
      amount: '$50.00',
      paymentFrequency: 'mensual',
    },
  },
  [TemplateCode.MEMBERSHIP_RENEWAL]: {
    code: TemplateCode.MEMBERSHIP_RENEWAL,
    title: 'Renovación de Membresía',
    description: 'Confirmación de renovación',
    icon: 'RefreshCw',
    variables: ['clientName', 'planName', 'newEndDate', 'amount'],
    exampleValues: {
      clientName: 'Carlos Ruiz',
      planName: 'Básico',
      newEndDate: '15/04/2024',
      amount: '$30.00',
    },
  },
  [TemplateCode.MEMBERSHIP_EXPIRING]: {
    code: TemplateCode.MEMBERSHIP_EXPIRING,
    title: 'Membresía Por Vencer',
    description: 'Recordatorio de expiración próxima',
    icon: 'AlertCircle',
    variables: ['clientName', 'planName', 'expirationDate', 'daysRemaining'],
    exampleValues: {
      clientName: 'Ana García',
      planName: 'Premium',
      expirationDate: '20/02/2024',
      daysRemaining: '3',
    },
  },
  [TemplateCode.PAYMENT_REMINDER]: {
    code: TemplateCode.PAYMENT_REMINDER,
    title: 'Recordatorio de Pago',
    description: 'Recordatorio de pago pendiente',
    icon: 'DollarSign',
    variables: ['clientName', 'amount', 'dueDate', 'planName'],
    exampleValues: {
      clientName: 'Pedro Sánchez',
      amount: '$40.00',
      dueDate: '25/02/2024',
      planName: 'Estándar',
    },
  },
  [TemplateCode.BIRTHDAY]: {
    code: TemplateCode.BIRTHDAY,
    title: 'Cumpleaños',
    description: 'Felicitación de cumpleaños',
    icon: 'Cake',
    variables: ['clientName', 'gymName', 'age'],
    exampleValues: {
      clientName: 'Laura Martínez',
      gymName: 'GymSpace',
      age: '28',
    },
  },
  [TemplateCode.PAYMENT_RECEIPT]: {
    code: TemplateCode.PAYMENT_RECEIPT,
    title: 'Comprobante de Pago',
    description: 'Comprobante de venta pagada',
    icon: 'Receipt',
    variables: [
      'gymName',
      'receiptNumber',
      'date',
      'clientName',
      'items',
      'amount',
      'paymentMethod',
      'saleItems',
      'currency',
    ],
    exampleValues: {
      gymName: 'GymSpace',
      receiptNumber: 'V-001',
      date: '15/01/2024',
      clientName: 'Juan Pérez',
      items: '• Proteína x2 = Q100.00\n• Creatina x1 = Q50.00',
      amount: 'Q150.00',
      paymentMethod: 'Efectivo',
      saleItems: [
        { product: { name: 'Proteína' }, quantity: 2, total: '100.00' },
        { product: { name: 'Creatina' }, quantity: 1, total: '50.00' },
      ],
      currency: 'Q',
    },
  },
  [TemplateCode.SHARE_CATALOG]: {
    code: TemplateCode.SHARE_CATALOG,
    title: 'Compartir Catálogo',
    description: 'Mensaje para compartir catálogo de membresías',
    icon: 'Share2',
    variables: ['clientName', 'gymName', 'urlCatalog'],
    exampleValues: {
      clientName: 'Roberto Gómez',
      gymName: 'GymSpace',
      urlCatalog: 'https://gymspace.app/catalog/gym-123',
    },
  },
  [TemplateCode.SALE_PAYMENT_REMINDER]: {
    code: TemplateCode.SALE_PAYMENT_REMINDER,
    title: 'Recordatorio de Pago de Venta',
    description: 'Recordatorio para ventas con pago pendiente',
    icon: 'AlertCircle',
    variables: [
      'clientName',
      'saleNumber',
      'total',
      'saleDate',
      'daysOverdue',
      'items',
      'saleItems',
      'currency',
    ],
    exampleValues: {
      clientName: 'Manuel Trainer',
      saleNumber: '#202511130003',
      total: 'S/ 205.00',
      saleDate: '13/11/2025',
      daysOverdue: '3',
      items: '• BCAA x2\n• Pre-Entreno x1',
      saleItems: [
        { product: { name: 'BCAA' }, quantity: 2 },
        { product: { name: 'Pre-Entreno' }, quantity: 1 },
      ],
      currency: 'S/ ',
    },
  },
} as const;
